import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {MenuItem, Tooltip, useMediaQuery} from '@mui/material';
import axios from 'axios';
import {useTheme} from '@mui/material/styles';
import {useLocation, useNavigate} from "react-router-dom";
import {collectionService} from '../../../services/collection.service';
import {useTranslation} from 'react-i18next';

import {
    AllDataSourcesButton,
    ConversationHistorySection,
    ConversationList,
    Divider,
    HistoryHeader,
    LogoBox,
    MobileMenuButton,
    ProfileContainer,
    ProfileMoreBtn,
    ProjectItem,
    ProjectName,
    ProjectsHeader,
    SidebarContainer,
    SidebarContent,
    SidebarHeader,
    StyledIconButton,
    StyledMenu,
    StyledNewRequestButton,
    StyledNewRequestButtonContent,
    StyledReportProblemButton,
    StyledReportProblemWrapper,
    StyledSectionHeader
} from './Sidebar.styled';

import {ReactComponent as AlertSvg} from "../../../assets/icons/alert.svg";
import {ReactComponent as FolderSvg} from "../../../assets/icons/catalog.svg";
import {ReactComponent as ChevronRightSvg} from "../../../assets/icons/chevron-right.svg";
import {ReactComponent as EditSvg} from "../../../assets/icons/edit.svg";
import {ReactComponent as ListSvg} from "../../../assets/icons/list.svg";
import {ReactComponent as MenuSvg} from "../../../assets/icons/menu.svg";
import {ReactComponent as MoreSvg} from "../../../assets/icons/more.svg";
import {ReactComponent as PinSvg} from "../../../assets/icons/pin.svg";
import {ReactComponent as TrashSvg} from "../../../assets/icons/trash.svg";

import Logo from "../../../assets/images/taia_logo.png";

import {useAppConstants, USER_ROLES} from '../../../config/appConstants';
import {PageTitleKeys, pageTitles} from '../../../config/routeConstants';
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {useAuth} from "../../../core/AuthContext";
import {useUser} from "../../../core/UserContext";
import {ConversationResponse, DataSource} from '../../../models/models';
import {parseError} from "../../../services/http.service";
import AdminDropdown from "../../admin/AdminDropdown";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import ReportProblemDialog from "../../dialogs/ReportProblemDialog";
import ConversationItem from './components/ConversationItem';

const Sidebar = ({isOpen, onToggle}: { isOpen: boolean; onToggle: () => void }) => {
    const {t} = useTranslation();
    const {user, fetchUserData, refreshConversations} = useUser();
    const {logout, isAdmin} = useAuth();
    const {showSnackbar} = useSnackbar();
    const isMobile = useMediaQuery('(max-width: 959px)');
    const [isSystemAdmin, setIsSystemAdmin] = useState(false);
    const [conversations, setConversations] = useState<ConversationResponse[]>([]);
    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [newTitle, setNewTitle] = useState('');
    const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState<ConversationResponse | null>(null);
    const [isReportProblemDialogOpen, setIsReportProblemDialogOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedConversation, setSelectedConversation] = useState<ConversationResponse | null>(null);

    const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const {DEFAULT_CONVERSATION_TITLE_PREFIX, CONVERSATION_GROUP_SECTIONS} = useAppConstants()

    const navigate = useNavigate();
    const location = useLocation();
    const isActive = location.pathname === "/chat";
    const isDataSourcesListActive = location.pathname === "/data-sources/list";
    const isProfileActive = location.pathname === "/account-settings";
    const theme = useTheme();

    const setEditingIdCallback = useCallback((id: number | null) => {
        setEditingId(id);
    }, []);

    const setNewTitleCallback = useCallback((title: string) => {
        setNewTitle(title);
    }, []);

    const setConversationsCallback = useCallback((updater: React.SetStateAction<ConversationResponse[]>) => {
        setConversations(updater);
    }, []);

    const setSelectedConversationCallback = useCallback((convo: ConversationResponse | null) => {
        setSelectedConversation(convo);
    }, []);

    const setMenuAnchorElCallback = useCallback((el: HTMLElement | null) => {
        setMenuAnchorEl(el);
    }, []);

    useEffect(() => {
        if (!user) {
            void fetchUserData();
            return;
        }
        setIsSystemAdmin(user.role === USER_ROLES.SYSTEM_ADMIN);
    }, [user]);

    useEffect(() => {
        setIsSystemAdmin(isAdmin);
    }, [isAdmin]);

    useEffect(() => {
        if (!isOpen) return;

        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        fetchTimeoutRef.current = setTimeout(() => {
            void fetchConversations();
            void fetchDataSources();
        }, 300);

        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [isOpen, refreshConversations]);

    // todo: refactor - dataSources should have a single source of truth (f.e. in context/service) and be set/get from
    //       there, that will enable bug free caching. Right now the cache causes bugs across the app.
    // todo: refactor all '/documents/by-collection' instances to control 'size' and 'omitCache' arguments for that
    //       single source of truth.

    const fetchDataSources = async () => {
        try {
            const data = await collectionService.fetchCollections(0, 200, true);
            const sortedDataSources = [...data.content].sort((a, b) => {
                return new Date(b.lastModificationDate).getTime() - new Date(a.lastModificationDate).getTime()
            });
            setDataSources(sortedDataSources.slice(0, 3));
        } catch (error) {
            showSnackbar(parseError(error), "error");
            setDataSources([]);
        }
    };

    const fetchConversations = async () => {
        try {
            const response = await axios.get('/chat/conversation/history');
            setConversations(response.data);
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    const handleLogoutClick = () => {
        setProfileMenuAnchor(null);
        logout();
    };

    const handleNewConversation = async () => {
        navigate(`/chat`);
    };

    const openDeleteDialog = (convo: ConversationResponse, event: React.MouseEvent) => {
        event.stopPropagation();
        setConversationToDelete(convo);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConversation = async () => {
        if (!conversationToDelete) return;

        try {
            await axios.delete(`/chat/conversation/${conversationToDelete.id}`);
            await fetchConversations();
            setIsDeleteDialogOpen(false);
            setConversationToDelete(null);
            showSnackbar(t('sidebar.conversationDeleted'), 'success');
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    const togglePin = async (convo: ConversationResponse, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            if (convo.pinned) {
                await axios.patch(`/chat/conversation/${convo.id}/unpin`);
            } else {
                await axios.patch(`/chat/conversation/${convo.id}/pin`);
            }
            await fetchConversations();
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    const groupedConversations = useMemo(() => {
        const buildConversationGroups = (conversations: ConversationResponse[]) => {
            const now = new Date();
            const groups: Record<string, ConversationResponse[]> = {
                [CONVERSATION_GROUP_SECTIONS.PINNED]: [],
                [CONVERSATION_GROUP_SECTIONS.TODAY]: [],
                [CONVERSATION_GROUP_SECTIONS.YESTERDAY]: [],
                [CONVERSATION_GROUP_SECTIONS.LAST_7_DAYS]: [],
                [CONVERSATION_GROUP_SECTIONS.OLDER]: [],
            };

            const sorted = [...conversations].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            for (const convo of sorted) {
                if (convo.pinned) {
                    groups[CONVERSATION_GROUP_SECTIONS.PINNED].push(convo);
                    continue;
                }

                const convoDate = new Date(convo.createdAt);
                const diffDays = Math.floor(
                    (now.getTime() - convoDate.getTime()) / (1000 * 60 * 60 * 24)
                );

                if (diffDays === 0) {
                    groups[CONVERSATION_GROUP_SECTIONS.TODAY].push(convo);
                } else if (diffDays === 1) {
                    groups[CONVERSATION_GROUP_SECTIONS.YESTERDAY].push(convo);
                } else if (diffDays <= 7) {
                    groups[CONVERSATION_GROUP_SECTIONS.LAST_7_DAYS].push(convo);
                } else {
                    groups[CONVERSATION_GROUP_SECTIONS.OLDER].push(convo);
                }
            }

            return groups;
        };

        return buildConversationGroups(conversations);
    }, [conversations]);

    const [currentChatId, setCurrentChatId] = useState<number | null>(null);

    useEffect(() => {
        const pathParts = location.pathname.split('/');
        if (location.pathname.startsWith('/chat/') && pathParts.length === 3) {
            const id = parseInt(pathParts[2], 10);
            setCurrentChatId(isNaN(id) ? null : id);
        } else {
            setCurrentChatId(null);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (currentChatId !== null) {
            const activeConversation = conversations.find(convo => convo.id === currentChatId);
            if (activeConversation?.title) {
                document.title = activeConversation.title;
            } else if (activeConversation) {
                document.title = `${DEFAULT_CONVERSATION_TITLE_PREFIX}${activeConversation.id}`;
            } else {
                document.title = t(pageTitles[PageTitleKeys.ChatWithId]);
            }
        } else if (location.pathname === '/chat') {
            document.title = t(pageTitles[PageTitleKeys.Chat]);
        }
    }, [currentChatId, conversations, location.pathname, t]);

    return (
        <>
            {isMobile && !isOpen && (
                <Tooltip title={t('sidebar.expand')}>
                    <MobileMenuButton onClick={onToggle}>
                        <MenuSvg />
                    </MobileMenuButton>
                </Tooltip>
            )}
            <SidebarContainer
                variant={isMobile ? "temporary" : "permanent"}
                open={isOpen}
                onClose={isMobile ? onToggle : undefined}
                ModalProps={{
                    keepMounted: true,
                }}
            >
            <SidebarHeader $isOpen={isOpen}>
                {isOpen &&
                    <LogoBox>
                        <img src={Logo} alt={"Logo"}/>
                    </LogoBox>
                }
                <Tooltip title={isOpen ? t('sidebar.collapse') : t('sidebar.expand')}>
                    <StyledIconButton onClick={onToggle}>
                        <div style={{color: theme.palette.primary.main, width: '25px', height: '25px'}}>
                            <MenuSvg/>
                        </div>
                    </StyledIconButton>
                </Tooltip>
            </SidebarHeader>

            <SidebarContent open={isOpen}>
                <Tooltip title={t('sidebar.chatStartMessageTooltip')}>
                    <StyledNewRequestButton open={isOpen} $active={isActive} onClick={handleNewConversation}>
                        <StyledNewRequestButtonContent $active={isActive}>
                            <div>
                                <EditSvg/>
                            </div>
                            {isOpen && <span>{t('sidebar.chatStartMessage')}</span>}
                            {isOpen && <div style={{marginLeft: 'auto'}}>
                                <ChevronRightSvg/>
                            </div>}
                        </StyledNewRequestButtonContent>
                    </StyledNewRequestButton>
                </Tooltip>

                <Divider/>

                {isSystemAdmin && <AdminDropdown isOpen={isOpen}/>}

                {isSystemAdmin && <Divider/>}

                {isOpen && dataSources.length > 0 && (
                    <>
                        <ProjectsHeader>
                            {t('sidebar.dataSources')}
                            <Tooltip title={t('sidebar.goToAllDataSources')}>
                                <AllDataSourcesButton
                                    onClick={() => navigate('/data-sources/list')}
                                    disabled={isDataSourcesListActive}
                                >
                                    <ListSvg/>
                                </AllDataSourcesButton>
                            </Tooltip>
                        </ProjectsHeader>
                        {dataSources.slice(0, 3).map((dataSource) => (
                            <ProjectItem
                                key={dataSource.id}
                                onClick={() => navigate(`/data-sources/${dataSource.id}/documents`)}
                                style={{cursor: 'pointer', transition: 'background-color 0.2s'}}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >

                                <ProjectName>
                                    <div style={{color: "black"}}>
                                        <FolderSvg/>
                                    </div>
                                    <span>{dataSource.name}</span>
                                </ProjectName>
                            </ProjectItem>
                        ))}
                        <Divider/>
                    </>
                )}

                {isOpen && conversations?.length > 0 && (
                    <ConversationHistorySection>
                        <HistoryHeader>{t('sidebar.history')}</HistoryHeader>
                        <ConversationList open={isOpen}>
                            {Object.entries(groupedConversations).map(([period, periodConversations]) => {
                                if (!periodConversations.length) return null;

                                return (
                                    <div key={period}>
                                        <StyledSectionHeader>{period}</StyledSectionHeader>

                                        {periodConversations.map((convo) => (
                                            <ConversationItem
                                                key={convo.id}
                                                convo={convo}
                                                newTitle={newTitle}
                                                isOpen={isOpen}
                                                currentChatId={currentChatId}
                                                editingId={editingId}
                                                setMenuAnchorEl={setMenuAnchorElCallback}
                                                setEditingId={setEditingIdCallback}
                                                setNewTitle={setNewTitleCallback}
                                                setConversations={setConversationsCallback}
                                                setSelectedConversation={setSelectedConversationCallback}
                                            />
                                        ))
                                        }
                                    </div>
                                );
                            })}
                        </ConversationList>
                    </ConversationHistorySection>
                )}
            </SidebarContent>

            <StyledReportProblemWrapper open={isOpen}>
                <Tooltip title={t('sidebar.goToReportProblem')}>
                    <StyledReportProblemButton open={isOpen} onClick={() => setIsReportProblemDialogOpen(true)}>
                        <div style={{color: theme.palette.primary.main, position: 'relative', top: '3px'}}>
                            <AlertSvg/>
                        </div>
                        {isOpen && <span>{t('sidebar.reportProblem')}</span>}
                        {isOpen && <ChevronRightSvg/>}
                    </StyledReportProblemButton>
                </Tooltip>
            </StyledReportProblemWrapper>
            <ReportProblemDialog open={isReportProblemDialogOpen} onClose={() => setIsReportProblemDialogOpen(false)}/>

            <ProfileContainer>
                {user?.avatarBase64 ? (
                    <img
                        src={user.avatarBase64}
                        alt="Profile"
                        style={{width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover'}}
                    />
                ) : (
                    <div
                        style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#1976d2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}
                    >
                        {user?.name && user?.surname
                            ? `${user.name.charAt(0)}${user.surname.charAt(0)}`
                            : (user?.name
                                ? user.name.charAt(0)
                                : (user?.email ? user.email.charAt(0).toUpperCase() : '?'))}
                    </div>
                )}
                <Tooltip title={t('sidebar.moreProfileOptions')}>
                    <ProfileMoreBtn onClick={(e) => setProfileMenuAnchor(e.currentTarget)}>
                        <div style={{color: 'black'}}>
                            <MoreSvg/>
                        </div>
                    </ProfileMoreBtn>
                </Tooltip>
            </ProfileContainer>

            <StyledMenu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={() => setProfileMenuAnchor(null)}
            >
                <MenuItem
                    onClick={() => {
                        setProfileMenuAnchor(null);
                        navigate('/account-settings');
                    }}
                    disabled={isProfileActive}
                >
                    {t('sidebar.accountSettings')}
                </MenuItem>
                <MenuItem onClick={handleLogoutClick}>{t('sidebar.logout')}</MenuItem>
            </StyledMenu>

            <StyledMenu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
            >
                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    if (selectedConversation) {
                        togglePin(selectedConversation, e);
                    }
                    setMenuAnchorEl(null);
                }}>
                    <PinSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>
                    <span style={{
                        color: '#474747',
                        fontSize: '14px'
                    }}>{selectedConversation?.pinned ? t('sidebar.unpin') : t('sidebar.pin')}</span>
                </MenuItem>
                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    if (selectedConversation) {
                        setEditingIdCallback(selectedConversation.id);
                        setNewTitleCallback(selectedConversation.title || `${DEFAULT_CONVERSATION_TITLE_PREFIX}${selectedConversation.id}`);
                    }
                    setMenuAnchorEl(null);
                }}>
                    <EditSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>
                    <span style={{color: '#474747', fontSize: '14px'}}>{t('sidebar.changeName')}</span>
                </MenuItem>
                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    if (selectedConversation) {
                        openDeleteDialog(selectedConversation, e);
                    }
                    setMenuAnchorEl(null);
                }}>
                    <TrashSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>
                    <span style={{color: '#474747', fontSize: '14px'}}>{t('sidebar.delete')}</span>
                </MenuItem>
            </StyledMenu>

            {conversationToDelete && (
                <ConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onConfirm={handleDeleteConversation}
                    title={t('sidebar.deleteConfirmationTitle')}
                    message={
                        <>
                            {t('sidebar.deleteConfirmationMessage')} &ldquo;
                            {conversationToDelete.title || `${DEFAULT_CONVERSATION_TITLE_PREFIX}${conversationToDelete.id}`}
                            &rdquo;?
                        </>
                    }
                    confirmButtonText={t('sidebar.deleteButton') as string}
                    cancelButtonText={t('sidebar.cancelButton') as string}
                />
            )}
            </SidebarContainer>
        </>
    );
};

export default Sidebar;
